// src/common/verification/parse-stage.js
import { extractErrorMessage } from '../utils/error-utils.js';
import { extractClaimFromInlineCitation } from '../utils/text-utils.js';
import { renderInitialCitationPlaceholders, updateCitationUI } from '../ui.js';

/**
 * Parse a footnote into citations.
 * Handles caching, inline citations, and API calls.
 * 
 * @param {Object} footnote - The footnote object to parse
 * @param {Object} options - Configuration options
 * @param {string} options.apiBase - API base URL
 * @param {Function} options.getAuthHeaders - Function to get auth headers
 * @param {boolean} options.isWebApp - Whether running in web app mode
 * @param {Object} options.commonPayload - Common payload for API calls
 * @param {boolean} options.skipCache - Whether to skip cache (for "Check Again")
 * @param {HTMLElement} options.resultsContainer - Container element for UI updates (optional)
 * @returns {Promise<{claim: string, citations: Array}>} Parsed claim and citations
 */
export async function parseFootnote(footnote, options = {}) {
  const {
    apiBase,
    getAuthHeaders,
    isWebApp = false,
    commonPayload = {},
    skipCache = false,
    resultsContainer = null,
  } = options;

  let claim;
  let citations;

  // Check for cached citations first (skip if Check Again)
  if (!skipCache && footnote.cached_citations && Array.isArray(footnote.cached_citations) && footnote.cached_citations.length > 0) {
    console.log('[ParseStage] Using cached citations for footnote', footnote.id);
    citations = footnote.cached_citations;
    
    // For inline citations, use the claim from the citation if available (LLM-extracted)
    // Otherwise, fall back to mechanical extraction
    if (footnote.is_inline) {
      const fullContext = footnote.context || '';
      const firstCitation = citations[0];
      const citationText = firstCitation?.source?.raw || '';
      
      // Check if the citation has a claim (extracted by LLM during parsing)
      if (firstCitation?.claim && firstCitation.claim.trim()) {
        claim = firstCitation.claim;
        console.log(`[ParseStage] Using LLM-extracted claim from cached citation: "${claim.substring(0, 100)}..."`);
      } else if (citationText && fullContext) {
        // Fallback to mechanical extraction
        claim = extractClaimFromInlineCitation(fullContext, citationText, citations);
        console.log(`[ParseStage] Extracted claim mechanically from cached citation (fallback): "${claim.substring(0, 100)}..."`);
      } else {
        claim = fullContext;
      }
    } else {
      // Footnotes: prefer the per-citation LLM-extracted claim, then the
      // backfilled footnote-level claim; legacy context only as a last resort.
      claim = citations.find((c) => c?.claim?.trim())?.claim
        || footnote.claim
        || citations[0]?.context
        || footnote.context
        || '';
    }

    if (resultsContainer) {
      renderInitialCitationPlaceholders(resultsContainer, citations);
    }
    return { claim, citations };
  }

  // Check if this is an inline citation with parsed data
  if (footnote.is_inline && footnote.parsed_citations) {
    console.log('[ParseStage] Inline citation detected, using parsed_citations');
    
    // Set citations to parsed_citations
    citations = footnote.parsed_citations;
    
    // For inline citations, use the claim from the citation if available (LLM-extracted)
    // Otherwise, fall back to mechanical extraction
    const fullContext = footnote.context || '';
    const firstCitation = citations[0];
    const citationText = firstCitation?.source?.raw || '';
    
    // Check if the citation has a claim (extracted by LLM during parsing)
    if (firstCitation?.claim && firstCitation.claim.trim()) {
      claim = firstCitation.claim;
      console.log(`[ParseStage] Using LLM-extracted claim from citation: "${claim.substring(0, 100)}..."`);
    } else if (citationText && fullContext) {
      // Fallback to mechanical extraction
      claim = extractClaimFromInlineCitation(fullContext, citationText, citations);
      console.log(`[ParseStage] Extracted claim mechanically (fallback): "${claim.substring(0, 100)}..."`);
    } else {
      claim = fullContext;
    }
    
    // No longer need to normalize types - backend handles this properly now
    // All types are returned as objects with proper structure
    if (resultsContainer) {
      renderInitialCitationPlaceholders(resultsContainer, citations);
    }
    return { claim, citations };
  }

  // Call the API to parse
  if (resultsContainer) {
    updateCitationUI(resultsContainer, null, { state: 'parsing', message: 'Parsing citations...' });
  }

  const endpoint = isWebApp ? '/web/audit/parse_citations' : '/audit/parse_citations';
  let res;

  if (isWebApp) {
    const { requestWebAudit } = await import('../../web-app/handlers/pdf-parsing.js');
    res = await requestWebAudit(apiBase, endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ footnote, ...commonPayload }),
    });
  } else {
    const requestBody = JSON.stringify({ footnote, ...commonPayload });
    const sendParseRequest = async (authOptions = {}) => {
      const authHeaders = await getAuthHeaders(authOptions);
      return fetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: requestBody,
      });
    };

    res = await sendParseRequest();
    if (res.status === 401) {
      console.warn(`[ParseStage] 401 for footnote ${footnote.id}; refreshing token and retrying once`);
      res = await sendParseRequest({ forceRefresh: true });
    }
  }
  
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.detail || extractErrorMessage(data) || 'Failed to parse citations.');
  }

  citations = Array.isArray(data?.citations) ? data.citations : [];
  claim = data.claim
    || citations.find((c) => c?.claim?.trim())?.claim
    || footnote.claim
    || footnote.context
    || '';
  
  if (resultsContainer) {
    renderInitialCitationPlaceholders(resultsContainer, citations);
  }

  return { claim, citations };
}
